import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { QRCodeSVG } from "qrcode.react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import Navbar from "@/components/Navbar";
import { LuxSolutionsLogo } from "@/components/ProjectInquiry";
import { Award, Printer, ArrowRight, Lock, Lightbulb, ShieldCheck, Copy, Check, Linkedin, ExternalLink } from "lucide-react";

function formatDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("de-CH", { day: "2-digit", month: "long", year: "numeric" });
  } catch {
    return iso;
  }
}

function LockedState({ d }) {
  const pct = d?.overall_percent ?? 0;
  return (
    <div data-testid="certificate-locked" className="rounded-3xl glass border border-white/10 px-6 py-14 flex flex-col items-center text-center">
      <div className="h-16 w-16 rounded-2xl bg-gold/10 border border-gold/25 flex items-center justify-center mb-5">
        <Lock className="h-8 w-8 text-gold" />
      </div>
      <h2 className="font-heading font-bold text-2xl">Zertifikat noch nicht freigeschaltet</h2>
      <p className="text-sm text-muted-foreground mt-3 max-w-md">
        Schliessen Sie alle {d?.modules_total ?? 10} Module ab – jeweils alle Lektionen plus das bestandene Quiz –, um Ihr persönliches Abschlusszertifikat zu erhalten.
      </p>
      <div className="w-full max-w-sm mt-7">
        <div className="flex items-center justify-between text-xs text-muted-foreground mb-1.5">
          <span>{d?.modules_complete ?? 0}/{d?.modules_total ?? 10} Module abgeschlossen</span>
          <span className="text-gold font-mono">{pct}%</span>
        </div>
        <div className="h-2.5 rounded-full bg-white/5 overflow-hidden">
          <div className="h-full rounded-full transition-all duration-700" style={{ width: `${pct}%`, background: "linear-gradient(90deg,#F5A623,#D4AF37)" }} />
        </div>
      </div>
      <Link to="/dashboard" className="mt-8 inline-flex items-center gap-2 px-6 py-3 rounded-full bg-gold text-[#0a0d12] font-semibold hover:shadow-[0_8px_30px_-8px_rgba(212,175,55,0.7)] transition-all duration-300">
        Weiterlernen <ArrowRight className="h-4 w-4" />
      </Link>
    </div>
  );
}

function CertificateDoc({ d, verifyUrl }) {
  return (
    <div
      id="certificate"
      data-testid="certificate-doc"
      className="relative rounded-3xl overflow-hidden grain"
      style={{ background: "#0a0d12", border: "2px solid rgba(212,175,55,0.45)", WebkitPrintColorAdjust: "exact", printColorAdjust: "exact" }}
    >
      <div className="absolute inset-0 radial-fade pointer-events-none" />
      <div className="absolute inset-3 rounded-2xl border border-gold/15 pointer-events-none" />
      <div className="relative px-8 sm:px-16 py-12 sm:py-16 text-center">
        <div className="flex items-center justify-center gap-2.5">
          <Lightbulb className="h-5 w-5 text-gold" />
          <span className="font-heading font-extrabold tracking-tight text-lg">LUX</span>
          <span className="text-[10px] uppercase tracking-[0.3em] text-muted-foreground">Lighting Academy</span>
        </div>

        <div className="mx-auto my-8 h-20 w-20 rounded-full bg-gold/10 border border-gold/30 flex items-center justify-center">
          <Award className="h-10 w-10 text-gold" />
        </div>

        <div className="text-[11px] uppercase tracking-[0.4em] text-gold">Abschlusszertifikat</div>
        <h1 className="font-heading font-extrabold text-3xl sm:text-4xl mt-3">Zertifikat über den erfolgreichen Abschluss</h1>

        <p className="text-foreground/65 mt-8">Hiermit wird bestätigt, dass</p>
        <div className="font-heading font-extrabold text-3xl sm:text-5xl text-gold my-4 glow-text">{d.name || "Teilnehmer:in"}</div>

        <p className="text-foreground/75 max-w-xl mx-auto leading-relaxed">
          den Kurs <strong className="text-foreground">„LUX Architect Lighting Academy"</strong> – professionelle Lichtplanung für
          Architekt:innen – mit allen {d.modules_total} Modulen, Lektionen und Quiz erfolgreich abgeschlossen hat.
        </p>

        <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-8 sm:gap-16">
          <div className="text-center">
            <div className="font-heading font-bold text-foreground">{formatDate(d.completed_at)}</div>
            <div className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground mt-1 pt-2 border-t border-white/10 px-4">Abschlussdatum</div>
          </div>
          <div className="text-center">
            <div className="flex items-center justify-center h-7"><LuxSolutionsLogo imgClassName="h-9 w-auto" /></div>
            <div className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground mt-1 pt-2 border-t border-white/10 px-4">Akademie · Lux Solutions GmbH</div>
          </div>
        </div>

        {verifyUrl && (
          <div className="mt-10 pt-6 border-t border-white/10 flex flex-col sm:flex-row items-center justify-between gap-5">
            <div className="text-center sm:text-left">
              <div className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground">Zertifikat-Nr.</div>
              <div className="font-mono text-gold text-sm mt-1" data-testid="certificate-id">{d.certificate_id}</div>
              <div className="text-[11px] text-muted-foreground mt-2 max-w-xs">Echtheit jederzeit online prüfbar unter<br /><span className="text-foreground/70 break-all">{verifyUrl}</span></div>
            </div>
            <div className="flex flex-col items-center gap-1.5">
              <div className="bg-white p-2 rounded-lg">
                <QRCodeSVG value={verifyUrl} size={84} level="M" fgColor="#0a0d12" bgColor="#ffffff" />
              </div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Scannen zum Verifizieren</div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function VerifyShare({ d, verifyUrl }) {
  const certId = d.certificate_id;
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(verifyUrl);
      setCopied(true);
      toast.success("Verifizierungslink kopiert");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Konnte nicht kopiert werden.");
    }
  };
  const issued = d.completed_at ? new Date(d.completed_at) : null;
  const params = new URLSearchParams({
    startTask: "CERTIFICATION_NAME",
    name: d.course_title || "LUX Architect Lighting Academy",
    organizationName: "Lux Solutions GmbH",
    certUrl: verifyUrl,
    certId: certId || "",
  });
  if (issued && !Number.isNaN(issued.getTime())) {
    params.set("issueYear", String(issued.getFullYear()));
    params.set("issueMonth", String(issued.getMonth() + 1));
  }
  const addToLinkedIn = `https://www.linkedin.com/profile/add?${params.toString()}`;

  return (
    <div className="no-print mt-6 rounded-2xl glass p-5 sm:p-6" data-testid="verify-share">
      <div className="flex items-center gap-2 mb-3">
        <ShieldCheck className="h-5 w-5 text-gold" />
        <h3 className="font-heading font-bold text-lg">Verifizieren & teilen</h3>
      </div>
      <p className="text-sm text-foreground/75 leading-relaxed">
        Ihr Zertifikat trägt die eindeutige Nummer <span className="font-mono text-gold">{certId}</span>. Über den Verifizierungslink kann jederzeit überprüft werden, dass es echt ist – ideal für LinkedIn oder Bewerbungen.
      </p>
      <div className="flex flex-col sm:flex-row gap-2 mt-4">
        <input
          readOnly
          value={verifyUrl}
          data-testid="verify-link-input"
          onFocus={(e) => e.target.select()}
          className="flex-1 px-4 py-2.5 rounded-full bg-white/5 border border-white/10 text-sm font-mono text-foreground/80 focus:outline-none focus:ring-2 focus:ring-gold/40"
        />
        <button onClick={copy} data-testid="copy-verify-link" className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-full bg-gold text-[#0a0d12] text-sm font-semibold hover:shadow-[0_8px_30px_-8px_rgba(212,175,55,0.7)] transition-all duration-300">
          {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />} {copied ? "Kopiert" : "Link kopieren"}
        </button>
      </div>

      <div className="mt-4 pt-4 border-t border-white/10">
        <p className="text-sm text-foreground/75 leading-relaxed mb-3">
          Tragen Sie Ihren Abschluss mit einem Klick in Ihr <strong className="text-foreground">LinkedIn-Profil</strong> ein – Kurs, Aussteller, Zertifikat-Nr. und Verifizierungslink sind bereits vorausgefüllt.
        </p>
        <div className="flex flex-wrap gap-2">
          <a
            href={addToLinkedIn}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="add-to-linkedin"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold text-white transition-all duration-300 hover:opacity-90"
            style={{ background: "#0A66C2" }}
          >
            <Linkedin className="h-4 w-4" /> Zu LinkedIn-Profil hinzufügen
          </a>
          <a href={verifyUrl} target="_blank" rel="noopener noreferrer" data-testid="open-verify" className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full glass text-sm hover:text-gold transition-colors">
            <ExternalLink className="h-4 w-4 text-gold" /> Verifizierungsseite öffnen
          </a>
        </div>
      </div>
    </div>
  );
}

export default function Certificate() {
  const certQ = useQuery({ queryKey: ["certificate"], queryFn: async () => (await api.get("/certificate")).data });
  const d = certQ.data;
  const verifyUrl = d?.certificate_id ? `${window.location.origin}/verify/${d.certificate_id}` : "";

  return (
    <div className="min-h-screen bg-background grain">
      <div className="no-print"><Navbar /></div>
      <main className="max-w-3xl mx-auto px-5 sm:px-8 py-8 sm:py-10">
        {certQ.isLoading ? (
          <div className="h-96 rounded-3xl glass animate-pulse" />
        ) : !d?.eligible ? (
          <LockedState d={d} />
        ) : (
          <>
            <div className="no-print flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
              <div>
                <h1 className="font-heading font-extrabold text-3xl">Ihr Zertifikat</h1>
                <p className="text-sm text-muted-foreground mt-1">Glückwunsch zum Abschluss – laden Sie Ihr Zertifikat als PDF herunter oder drucken Sie es aus.</p>
              </div>
              <button
                onClick={() => window.print()}
                data-testid="certificate-print"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-gold text-[#0a0d12] font-semibold hover:shadow-[0_8px_30px_-8px_rgba(212,175,55,0.7)] transition-all duration-300 flex-shrink-0"
              >
                <Printer className="h-4 w-4" /> Als PDF speichern
              </button>
            </div>
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
              <CertificateDoc d={d} verifyUrl={verifyUrl} />
            </motion.div>
            <VerifyShare d={d} verifyUrl={verifyUrl} />
          </>
        )}
      </main>
    </div>
  );
}
